import React, { useEffect } from 'react'
import styled from 'styled-components'
import { Nullable, isArray, isDefined } from 'shared/utils/type-guards'
import {
  ProjectViewEnum,
  TaskResponsibilityEnumType,
  UserProjectOrganizationTypeEnumType,
} from 'shared/common/global-types.runtime'
import { ProjectTypeEnumType } from 'shared/common/global-types.runtime'
import Logo from '~/views/components/Logo'
import VideoModalView from '~/views/components/VideoModalView'
import Text from '~/views/components/ui/Text'
import { useCurrentUserContext } from '~/views/contexts/CurrentUserContext'
import { useRouter } from 'next/router'

import ExperienceOverviewCard, {
  Props as ExperienceOverviewProps,
} from './components/ExperienceOverviewCard'
import ProjectManagerCard, {
  Props as ProjectManagerCardProps,
} from './components/ProjectManagerCard'
import SummaryCard, {
  Props as SummaryCardProps,
} from './components/SummaryCard'
import TaskSummaryCard, {
  Props as TaskSummaryCardProps,
} from './components/TaskSummaryCard/V2/TaskSummaryCardV2'
import TeamCard, { Props as TeamCardProps } from './components/TeamCard'
import TimeTrackingSummary, {
  TimeTrackingSummaryProps,
} from './components/TimeTrackingSummary'

const OverviewWrapper = styled.div(
  ({ theme }) => `
  width: 100%;
  height: 100%;
  ${theme.name === 'lightV2' ? `margin-bottom: ${theme.spacing(4)};` : ''}
`,
)

const OverviewGrid = styled.div(
  ({ theme }) => `
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: repeat(7, auto);
  grid-gap: ${theme.spacing(3)};
  grid-template-areas:
    'po po po po po'
    'po po po po po'
    'po po po po po'
    's  s  s  s  s'
    'eo eo pm pm pm'
    'eo eo tm tm tm'
    'eo eo tm tm tm'
    'ttsc ttsc ttsc ttsc ttsc';
`,
)

const StyledSummaryCard = styled(SummaryCard)`
  grid-area: po;
`

const TaskSummaryContainer = styled.div<{ disabled: boolean }>`
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  display: grid;
  width: 100%;
  grid-auto-flow: column;
  grid-area: s;
  grid-gap: ${({ theme }) => theme.spacing(3)};
`

const StyledExperienceOverviewCard = styled(ExperienceOverviewCard)`
  grid-area: eo;
`

const StyledProjectManagerCard = styled(ProjectManagerCard)`
  grid-area: pm;
`

const StyledTeamCard = styled(TeamCard)`
  grid-area: tm;
`

const PoweredByLogo = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  display: flex;
  justify-content: center;
  & > div:first-child {
    text-align: center;
  }
`
interface Props {
  project?: SummaryCardProps['project'] &
    ExperienceOverviewProps['project'] &
    TimeTrackingSummaryProps['project'] &
    ProjectManagerCardProps['project'] &
    TeamCardProps['project'] &
    TaskSummaryCardProps['project'] & {
      id: string
      projectType?: ProjectTypeEnumType
      currentUserProjectContext?: UserProjectOrganizationTypeEnumType
      currentUserSettings?: Nullable<{
        projectView?: Nullable<ProjectViewEnum>
      }>
      organization?: Nullable<{
        id: string
        name?: Nullable<string>
      }>
      templates?: Nullable<
        {
          id: string
          videoUrl?: Nullable<string>
        }[]
      >
    }
  stats?: SummaryCardProps['stats'] &
    TimeTrackingSummaryProps['stats'] & {
      internalTasks: Nullable<TaskSummaryCardProps['stats']>
      internalHours: Nullable<TaskSummaryCardProps['hours']>
      customerTasks: Nullable<TaskSummaryCardProps['stats']>
      thirdPartyTasks: Nullable<TaskSummaryCardProps['stats']>
    }
}

const ProjectOverview = ({ project, stats }: Props) => {
  const { currentUser } = useCurrentUserContext()
  const router = useRouter()

  const isInternalCustomerType =
    project?.projectType === ProjectTypeEnumType.Internal
  const withThirdPartyTasks = stats?.thirdPartyTasks?.remaining !== 0

  //this logic should be put in middleware, so it will redirect anywhere inside of /app/[projectId]/ and not only in /app/[projectId]/overview
  useEffect(() => {
    const isCustomerProjectOrganization =
      project?.currentUserProjectContext ===
      UserProjectOrganizationTypeEnumType.Customer
    const userHasOrganization = isDefined(currentUser?.organization)
    const projectUseCustomerPortal =
      project?.currentUserSettings?.projectView === ProjectViewEnum.Compass

    if (
      project?.id &&
      projectUseCustomerPortal &&
      userHasOrganization &&
      isCustomerProjectOrganization
    ) {
      router.push(`/customer/today/${project.id}`)
    }
  }, [project])
  const customer = project?.customer

  return (
    <OverviewWrapper>
      <OverviewGrid>
        <StyledSummaryCard project={project} stats={stats} />

        <TaskSummaryContainer
          disabled={!currentUser}
          data-testid={'task-summary-container'}
        >
          <TaskSummaryCard
            project={project}
            responsibility={TaskResponsibilityEnumType.Internal}
            stats={stats?.internalTasks}
            hours={stats?.internalHours}
            title={`${project?.displayOrganization?.name}`}
            isProvider={!isInternalCustomerType || withThirdPartyTasks}
          />

          {!isInternalCustomerType && (
            <TaskSummaryCard
              project={project}
              responsibility={TaskResponsibilityEnumType.Customer}
              title={customer?.name}
              stats={stats?.customerTasks}
            />
          )}

          {withThirdPartyTasks && (
            <TaskSummaryCard
              project={project}
              title="Third Party"
              responsibility={TaskResponsibilityEnumType.ThirdParty}
              stats={stats?.thirdPartyTasks}
            />
          )}
        </TaskSummaryContainer>

        <StyledExperienceOverviewCard
          project={project}
          organization={project?.organization}
          templates={project?.templates}
        />
        <StyledProjectManagerCard project={project} />

        <StyledTeamCard project={project} />

        <TimeTrackingSummary project={project} stats={stats} />
      </OverviewGrid>

      {isArray(project?.templates) && (
        <VideoModalView videoUrl={project?.templates[0]?.videoUrl || null} />
      )}

      {!currentUser && (
        <PoweredByLogo>
          <div>
            <Text>Powered by</Text>
            <br />
            <Logo height="64px" />
          </div>
        </PoweredByLogo>
      )}
    </OverviewWrapper>
  )
}

export default ProjectOverview
